import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Input,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnType } from 'antd/es/table';

import InternalBackupDashboardContext from '@totem/components/services/backup/internalDashboard/InternalBackupDashboardContext';
import { InternalBackupDashboardRecord } from '@totem/components/services/backup/internalDashboard/types';

const { Text } = Typography;

type BackupStatus = 'Current' | 'Stale' | 'Overdue' | 'Failed';

const computeStatus = (record: InternalBackupDashboardRecord): BackupStatus => {
  if (record.lastBackupErr) return 'Failed';
  if (!record.lastBackupAt) return 'Overdue';
  const ageDays =
    (Date.now() - new Date(record.lastBackupAt).getTime()) / 86400000;
  if (ageDays < 2) return 'Current';
  if (ageDays < 3) return 'Stale';
  return 'Overdue';
};

const isWatch = (record: InternalBackupDashboardRecord): boolean =>
  record.managedStatus === 'INACTIVE' &&
  record.contractStatus === 'Not on contract';

const isRetired = (record: InternalBackupDashboardRecord): boolean => {
  if (!record.lastBackupAt) return true;
  const ageDays = Math.floor(
    (Date.now() - new Date(record.lastBackupAt).getTime()) / 86400000,
  );
  return ageDays > 14 && record.contractStatus !== 'On contract';
};

const STATUS_COLOR: Record<BackupStatus, string> = {
  Current: 'success',
  Stale: 'warning',
  Overdue: 'error',
  Failed: 'error',
};

const STATUS_ORDER: Record<BackupStatus, number> = {
  Failed: 0,
  Overdue: 1,
  Stale: 2,
  Current: 3,
};

const TICKET_ROUTE = '/dashboard/ticket/';

const formatBytes = (bytes: number | null): string => {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const idx = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, idx)).toFixed(1)} ${units[idx]}`;
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const computeAgeDays = (iso: string | null): number | null => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};

interface KpiCardProps {
  label: string;
  value: number;
  sub: string;
  valueColor?: string;
}

const KpiCard = ({ label, value, sub, valueColor }: KpiCardProps) => (
  <div
    style={{
      background: 'var(--ant-color-bg-container)',
      border: '1px solid var(--ant-color-border)',
      borderRadius: 10,
      padding: '12px 14px',
      boxShadow: '0 1px 2px rgba(0,0,0,.05), 0 4px 12px rgba(0,0,0,.04)',
      minWidth: 0,
    }}
  >
    <div
      style={{
        fontSize: 10.5,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--ant-color-text-secondary)',
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 26,
        fontWeight: 700,
        color: valueColor ?? 'var(--ant-color-text)',
        marginTop: 4,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontSize: 11,
        color: 'var(--ant-color-text-secondary)',
        marginTop: 6,
      }}
    >
      {sub}
    </div>
  </div>
);

export const InternalBackupDashboardExtra = () => {
  const { loading, data } = useContext(InternalBackupDashboardContext);
  if (loading) return null;
  return <Text type="secondary">{data.length} devices</Text>;
};

const buildColumns = (): ColumnType<InternalBackupDashboardRecord>[] => [
  {
    title: 'Customer',
    dataIndex: 'orgName',
    key: 'orgName',
    ellipsis: true,
    sorter: (aRow, bRow) => aRow.orgName.localeCompare(bRow.orgName),
  },
  {
    title: 'Building',
    dataIndex: 'buildingName',
    key: 'buildingName',
    ellipsis: true,
    render: (val: string) => val || <Text type="secondary">—</Text>,
    sorter: (aRow, bRow) => aRow.buildingName.localeCompare(bRow.buildingName),
  },
  {
    title: 'Control System',
    dataIndex: 'csName',
    key: 'csName',
    ellipsis: true,
    render: (val: string) => val || <Text type="secondary">—</Text>,
    sorter: (aRow, bRow) => aRow.csName.localeCompare(bRow.csName),
  },
  {
    title: 'Device',
    dataIndex: 'displayName',
    key: 'displayName',
    ellipsis: true,
    render: (val: string, record: InternalBackupDashboardRecord) => (
      <Link to={`/dashboard/devices/${record.id}`}>{val}</Link>
    ),
    sorter: (aRow, bRow) => aRow.displayName.localeCompare(bRow.displayName),
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    ellipsis: true,
    sorter: (aRow, bRow) => aRow.type.localeCompare(bRow.type),
  },
  {
    title: 'Ticket',
    key: 'ticket',
    render: (_: unknown, record: InternalBackupDashboardRecord) => {
      if (!record.ticketNumber) return <Text type="secondary">—</Text>;
      const statusLabel = record.ticketStatus
        ? `(${record.ticketStatus.charAt(0).toUpperCase()}${record.ticketStatus.slice(1).toLowerCase()})`
        : '';
      const isOpen = !['CLOSED', 'RESOLVED'].includes(
        (record.ticketStatus ?? '').toUpperCase(),
      );
      const link = record.ticketId ? (
        <Link to={`${TICKET_ROUTE}${record.ticketId}`}>
          {record.ticketNumber}
        </Link>
      ) : (
        <span>{record.ticketNumber}</span>
      );
      return (
        <span>
          {link}
          {statusLabel && (
            <Text
              type={isOpen ? 'danger' : 'secondary'}
              style={{ fontSize: 11, marginLeft: 4 }}
            >
              {statusLabel}
            </Text>
          )}
        </span>
      );
    },
  },
  {
    title: 'Last Good Backup',
    dataIndex: 'lastBackupAt',
    key: 'lastBackupAt',
    render: (val: string | null) => (
      <span style={{ whiteSpace: 'nowrap' }}>{formatDate(val)}</span>
    ),
    sorter: (aRow, bRow) => {
      const aTime = aRow.lastBackupAt
        ? new Date(aRow.lastBackupAt).getTime()
        : 0;
      const bTime = bRow.lastBackupAt
        ? new Date(bRow.lastBackupAt).getTime()
        : 0;
      return bTime - aTime;
    },
  },
  {
    title: 'Age',
    key: 'age',
    render: (_: unknown, record: InternalBackupDashboardRecord) => {
      const days = computeAgeDays(record.lastBackupAt);
      if (days === null) return <Text type="secondary">—</Text>;
      if (days === 0) return 'Today';
      return `${days}d`;
    },
    sorter: (aRow, bRow) => {
      const aAge = computeAgeDays(aRow.lastBackupAt) ?? 9999;
      const bAge = computeAgeDays(bRow.lastBackupAt) ?? 9999;
      return bAge - aAge;
    },
  },
  {
    title: 'Size',
    dataIndex: 'lastBackupSize',
    key: 'lastBackupSize',
    render: (val: number | null) => (
      <span style={{ whiteSpace: 'nowrap' }}>{formatBytes(val)}</span>
    ),
    sorter: (aRow, bRow) =>
      (aRow.lastBackupSize ?? 0) - (bRow.lastBackupSize ?? 0),
  },
  {
    title: 'Status',
    key: 'status',
    render: (_: unknown, record: InternalBackupDashboardRecord) => {
      const status = computeStatus(record);
      const tag = <Tag color={STATUS_COLOR[status]}>{status}</Tag>;
      if (status === 'Failed' && record.lastBackupErr) {
        return <Tooltip title={record.lastBackupErr}>{tag}</Tooltip>;
      }
      return tag;
    },
    sorter: (aRow, bRow) =>
      STATUS_ORDER[computeStatus(aRow)] - STATUS_ORDER[computeStatus(bRow)],
    defaultSortOrder: 'ascend',
  },
  {
    title: 'Managed',
    dataIndex: 'managedStatus',
    key: 'managedStatus',
    render: (val: string) =>
      val ? (
        <Tag color={val === 'ACTIVE' ? 'success' : 'default'}>{val}</Tag>
      ) : (
        <Text type="secondary">—</Text>
      ),
  },
  {
    title: 'Contract',
    dataIndex: 'contractStatus',
    key: 'contractStatus',
    render: (val: string) => {
      const color =
        val === 'On contract'
          ? 'success'
          : val === 'Building only'
            ? 'blue'
            : 'error';
      return <Tag color={color}>{val}</Tag>;
    },
    sorter: (aRow, bRow) =>
      aRow.contractStatus.localeCompare(bRow.contractStatus),
  },
];

interface TabContentProps {
  rows: InternalBackupDashboardRecord[];
  searchText: string;
  orgFilter: string;
  buildingFilter: string;
  statusFilter: string;
  managedFilter: string;
  contractFilter: string;
}

const TabContent = ({
  rows,
  searchText,
  orgFilter,
  buildingFilter,
  statusFilter,
  managedFilter,
  contractFilter,
}: TabContentProps) => {
  const filtered = useMemo(() => {
    const query = searchText.toLowerCase();
    return rows.filter((rec) => {
      if (orgFilter && rec.orgName !== orgFilter) return false;
      if (buildingFilter && rec.buildingName !== buildingFilter) return false;
      if (statusFilter && computeStatus(rec) !== statusFilter) return false;
      if (managedFilter === 'WATCH') {
        if (!isWatch(rec)) return false;
      } else if (managedFilter && rec.managedStatus !== managedFilter) {
        return false;
      }
      if (contractFilter && rec.contractStatus !== contractFilter) return false;
      if (query) {
        const hay = [
          rec.displayName,
          rec.buildingName,
          rec.csName,
          rec.type,
          rec.orgName,
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    });
  }, [
    rows,
    searchText,
    orgFilter,
    buildingFilter,
    statusFilter,
    managedFilter,
    contractFilter,
  ]);

  const kpis = useMemo(() => {
    const total = filtered.length;
    const current = filtered.filter(
      (rec) => computeStatus(rec) === 'Current',
    ).length;
    const stale = filtered.filter(
      (rec) => computeStatus(rec) === 'Stale',
    ).length;
    const overdue = filtered.filter(
      (rec) => computeStatus(rec) === 'Overdue',
    ).length;
    const failed = filtered.filter(
      (rec) => computeStatus(rec) === 'Failed',
    ).length;
    return { total, current, stale, overdue, failed };
  }, [filtered]);

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <KpiCard
          label="Devices Backed Up"
          value={kpis.total}
          sub="All matching filter"
        />
        <KpiCard
          label="Current (<2d)"
          value={kpis.current}
          sub="Backed up within 2 days"
          valueColor="#52c41a"
        />
        <KpiCard
          label="Stale (2d)"
          value={kpis.stale}
          sub="Severity 2 trigger · 2 days old"
          valueColor={kpis.stale > 0 ? '#faad14' : undefined}
        />
        <KpiCard
          label="Overdue (≥3d)"
          value={kpis.overdue}
          sub="Severity 5 trigger · 3+ days old"
          valueColor={kpis.overdue > 0 ? '#ff4d4f' : undefined}
        />
        <KpiCard
          label="Failed"
          value={kpis.failed}
          sub="Last attempt has an error"
          valueColor={kpis.failed > 0 ? '#ff4d4f' : undefined}
        />
      </div>
      <Table<InternalBackupDashboardRecord>
        dataSource={filtered}
        columns={buildColumns()}
        rowKey="id"
        size="small"
        pagination={{ pageSize: 100, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
      />
    </>
  );
};

const InternalBackupDashboardLayout = () => {
  const { loading, data } = useContext(InternalBackupDashboardContext);

  const [searchText, setSearchText] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [buildingFilter, setBuildingFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [managedFilter, setManagedFilter] = useState('');
  const [contractFilter, setContractFilter] = useState('');

  const { activeRows, retiredRows } = useMemo(
    () => ({
      activeRows: data.filter((rec) => !isRetired(rec)),
      retiredRows: data.filter((rec) => isRetired(rec)),
    }),
    [data],
  );

  const orgOptions = useMemo(() => {
    const names = Array.from(
      new Set(data.map((rec) => rec.orgName).filter(Boolean)),
    ).sort();
    return [
      { value: '', label: 'All customers' },
      ...names.map((name) => ({ value: name, label: name })),
    ];
  }, [data]);

  const buildingOptions = useMemo(() => {
    const source = orgFilter
      ? data.filter((rec) => rec.orgName === orgFilter)
      : data;
    const names = Array.from(
      new Set(source.map((rec) => rec.buildingName).filter(Boolean)),
    ).sort();
    return [
      { value: '', label: 'All buildings' },
      ...names.map((name) => ({ value: name, label: name })),
    ];
  }, [data, orgFilter]);

  const handleOrgChange = (val: string) => {
    setOrgFilter(val);

    setBuildingFilter('');
  };

  const handleReset = () => {
    setSearchText('');

    setOrgFilter('');

    setBuildingFilter('');

    setStatusFilter('');

    setManagedFilter('');

    setContractFilter('');
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const filterProps = {
    searchText,
    orgFilter,
    buildingFilter,
    statusFilter,
    managedFilter,
    contractFilter,
  };

  const tabItems = [
    {
      key: 'active',
      label: `Active Backups (${activeRows.length})`,
      children: <TabContent rows={activeRows} {...filterProps} />,
    },
    {
      key: 'retired',
      label: `Retired / Off-Contract (${retiredRows.length})`,
      children: <TabContent rows={retiredRows} {...filterProps} />,
    },
  ];

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <Select
          options={orgOptions}
          value={orgFilter}
          onChange={handleOrgChange}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          style={{ minWidth: 160 }}
          placeholder="All customers"
        />
        <Select
          options={buildingOptions}
          value={buildingFilter}
          onChange={setBuildingFilter}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          style={{ minWidth: 160 }}
          placeholder="All buildings"
        />
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ minWidth: 130 }}
          options={[
            { value: '', label: 'All statuses' },
            { value: 'Current', label: 'Current' },
            { value: 'Stale', label: 'Stale' },
            { value: 'Overdue', label: 'Overdue' },
            { value: 'Failed', label: 'Failed' },
          ]}
        />
        <Select
          value={managedFilter}
          onChange={setManagedFilter}
          style={{ minWidth: 170 }}
          options={[
            { value: '', label: 'All managed' },
            { value: 'ACTIVE', label: 'Active' },
            { value: 'INACTIVE', label: 'Inactive' },
            { value: 'WATCH', label: '⚠ Agent + no contract' },
          ]}
        />
        <Select
          value={contractFilter}
          onChange={setContractFilter}
          style={{ minWidth: 160 }}
          options={[
            { value: '', label: 'All contracts' },
            { value: 'On contract', label: 'On contract' },
            { value: 'Building only', label: 'Building only' },
            { value: 'Not on contract', label: 'Not on contract' },
          ]}
        />
        <Input.Search
          placeholder="Device, building, control system…"
          allowClear
          value={searchText}
          onChange={(evt) => setSearchText(evt.target.value)}
          style={{ minWidth: 240, flex: '1 1 240px' }}
        />
        <a
          role="button"
          onClick={handleReset}
          style={{ fontSize: 13, whiteSpace: 'nowrap' }}
        >
          Reset
        </a>
      </div>
      <Tabs items={tabItems} />
    </>
  );
};

export default InternalBackupDashboardLayout;
